/**
 * EmailNoteCard — nota di tipo "email" nella cronologia del lead, generata dal
 * collegamento di un thread Gmail (note_type='email').
 *
 * Stessa anatomia delle altre card: tessera email nella colonna immagine,
 * oggetto in testa, mittente e anteprima sotto, riga in basso con numero di
 * messaggi, aperture, link "Apri in Gmail", chi l'ha collegata, ora e tipologia.
 */
import React, { useMemo } from 'react';
import { Mail, ExternalLink, X, Eye } from 'lucide-react';
import { useMailOpenStatusByThread } from '../../lib/hooks/useMailOpenStatus';
import { CHIP_DONE_CLS, CHIP_NEUTRAL_CLS, ACTION_BTN_CLS, mediaTileCls } from '../shared/timeline/cardStyles';
import { CardMedia } from '../shared/timeline/CardMedia';
import { CardActions } from '../shared/timeline/CardActions';
import { TimelineFooter } from '../shared/timeline/TimelineFooter';

interface EmailNoteMetadata {
  gmail_thread_id?: string;
  from_email?: string;
  from_name?: string | null;
  subject?: string | null;
  message_count?: number;
  snippet?: string | null;
}

interface EmailNoteCardProps {
  note: {
    id: string;
    metadata?: EmailNoteMetadata;
    created_at: string;
    author?: { full_name?: string; email?: string; profile_image_url?: string | null };
  };
  formatDate: (dateString: string) => string;
  onDelete?: (noteId: string) => void;
}

// Helper: una nota è di tipo email se ha note_type='email' o un thread Gmail nei metadata
export function isEmailNote(note: any): boolean {
  return note?.note_type === 'email' || !!note?.metadata?.gmail_thread_id;
}

export function EmailNoteCard({ note, formatDate, onDelete }: EmailNoteCardProps) {
  const md = note.metadata || {};
  const sender = (md.from_name || '').trim() || md.from_email || 'Mittente sconosciuto';
  const count = md.message_count || 1;
  const gmailUrl = md.gmail_thread_id
    ? `https://mail.google.com/mail/u/0/#all/${md.gmail_thread_id}`
    : null;

  // Stato apertura (tracking pixel) dei messaggi inviati del thread collegato
  const threadIds = useMemo(() => (md.gmail_thread_id ? [md.gmail_thread_id] : []), [md.gmail_thread_id]);
  const openInfo = useMailOpenStatusByThread(threadIds).get(md.gmail_thread_id || '');
  const autore = note.author?.full_name || note.author?.email || 'utente';

  return (
    <div className="group relative mb-3 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
      <div className="min-w-0 p-3">
        {onDelete && (
          <CardActions>
            <button
              onClick={() => onDelete(note.id)}
              className={`${ACTION_BTN_CLS} hover:text-[#D44C47]`}
              title="Elimina nota"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </CardActions>
        )}

        <CardMedia
          media={
            <span className={mediaTileCls('email')}>
              <Mail size={15} strokeWidth={1.5} />
            </span>
          }
        >
          <div className="truncate pr-8 text-sm font-medium text-[#37352F] dark:text-white" title={md.subject || undefined}>
            {md.subject || '(Nessun oggetto)'}
          </div>
          <div className="mt-0.5 truncate text-xs text-[#787774] dark:text-white/50">Da: {sender}</div>
          {md.snippet && (
            <p className="mt-1 line-clamp-2 text-xs text-[rgba(55,53,47,0.7)] dark:text-white/70">{md.snippet}</p>
          )}
        </CardMedia>

        <TimelineFooter
          tipo="Email"
          accent="email"
          date={note.created_at}
          titoloData={formatDate(note.created_at)}
          left={
            <>
              <span className={CHIP_NEUTRAL_CLS}>
                {count} {count === 1 ? 'messaggio' : 'messaggi'}
              </span>
              {openInfo && (
                <span
                  className={CHIP_DONE_CLS}
                  title={
                    openInfo.lastOpenedAt
                      ? `Ultima apertura: ${new Date(openInfo.lastOpenedAt).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                      : undefined
                  }
                >
                  <Eye className="h-2.5 w-2.5" />
                  Aperta {openInfo.openCount}×
                </span>
              )}
              {gmailUrl && (
                <a
                  href={gmailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] text-[#2383E2] hover:underline"
                  title="Apri il thread in Gmail"
                >
                  Apri in Gmail <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
              <span className="text-[10px] text-[#9B9A97] dark:text-white/40">Collegata da {autore}</span>
            </>
          }
        />
      </div>
    </div>
  );
}
